// Game logic for Wordsheet
import { Board } from './board.js';
import { TileBag, createTileElement, resolveBagSelection } from './tiles.js';
import { isValidWord } from './dictionary.js';
import { flags } from './flags.js';
import { celebrate, startAmbientFireworks } from './celebration.js';

export class Game {
    constructor(options = {}) {
        this.board = new Board();
        if (options.skipTileBag) {
            // Multiplayer: bag lives in Firestore; name is displayed by the multiplayer game
            this.tileBag = null;
            this.bagName = null;
        } else {
            const bagChoice = resolveBagSelection(flags.get('tileBag'));
            this.tileBag = new TileBag(bagChoice.key);
            this.bagName = bagChoice.name;
        }
        this.updateBagNameDisplay();
        this.rack = [];
        this.selectedTile = null;
        this.draggedTile = null;
        this.dragSource = null; // 'rack' or {row, col} for board
        this.score = 0;
        this.isFirstMove = true;
        this.isMultiplayer = options.isMultiplayer || false;

        // Tile picker state
        this.pickerElement = document.getElementById('tile-picker');
        this.pickerTargetCell = null;

        // Blank tile awaiting letter assignment
        this.pendingBlankTile = null;
        this.pendingBlankCell = null;
        this.blankLetterInput = null;
        this.eventHandlers = {};
        this.boundElements = {};
        this.submitButton = null;
        this.swapButton = null;
        this.skipButton = null;
        this.finishButton = null;
        this.controlsStack = null;
        this.recallButton = null;
        this.shuffleButton = null;

        // Swap mode state
        this.swapMode = false;
        this.tilesMarkedForSwap = new Set(); // stores tile IDs

        // For single player, init rack normally
        // For multiplayer, rack is set externally via loadRackState
        if (!this.isMultiplayer && this.tileBag) {
            document.getElementById('score').textContent = '0';
            this.initRack();
        }

        this.bindEvents();

        if (!this.isMultiplayer) {
            this.showMessage('Click a cell to pick a tile, or drag tiles to place them.', 'info');
        }
    }

    initRack() {
        this.rack = this.tileBag.draw(7);
        this.renderRack();
    }

    renderRack() {
        const rackElement = document.getElementById('rack');
        rackElement.innerHTML = '';

        this.rack.forEach((tile, index) => {
            const slot = document.createElement('div');
            slot.className = 'rack-slot';
            slot.dataset.index = index;

            const tileElement = createTileElement(tile);
            if (this.selectedTile && this.selectedTile.id === tile.id) {
                tileElement.classList.add('selected');
            }
            if (this.tilesMarkedForSwap.has(tile.id)) {
                tileElement.classList.add('marked-for-swap');
            }
            slot.appendChild(tileElement);
            rackElement.appendChild(slot);
        });

        this.updateSwapButtonText();
        this.updateTileBagCounter();
    }

    updateSwapButtonText() {
        if (!this.swapButton) return;
        const count = this.tilesMarkedForSwap.size;
        if (this.swapMode && count > 0) {
            this.swapButton.innerHTML = `Swap<br>(${count})`;
        } else {
            this.swapButton.innerHTML = 'Swap<br>Tiles';
        }
    }

    updateTileBagCounter() {
        const tilesRemainingEl = document.getElementById('tiles-remaining');
        if (tilesRemainingEl && this.tileBag) {
            tilesRemainingEl.textContent = this.tileBag.remaining();
        }
        this.updateEndgameButtons();
    }

    updateBagNameDisplay() {
        const bagNameEl = document.getElementById('tile-bag-name');
        if (bagNameEl) {
            bagNameEl.textContent = this.bagName || '';
        }
    }

    updateEndgameButtons() {
        // Check if tile bag is empty
        const bagEmpty = this.tileBag ? this.tileBag.remaining() === 0 : false;

        if (bagEmpty) {
            // Hide swap/skip, show finish
            if (this.controlsStack) this.controlsStack.classList.add('hidden');
            if (this.finishButton) this.finishButton.classList.remove('hidden');
        } else {
            // Show swap/skip, hide finish
            if (this.controlsStack) this.controlsStack.classList.remove('hidden');
            if (this.finishButton) this.finishButton.classList.add('hidden');
        }
    }

    finishGame() {
        this.showGameOver();
    }

    showGameOver() {
        // Remove existing scoreboard if any
        const existingScoreboard = document.getElementById('game-scoreboard');
        if (existingScoreboard) {
            existingScoreboard.remove();
        }

        // Fireworks + fanfare
        const cleanupCelebration = celebrate();

        // Create scoreboard overlay
        const overlay = document.createElement('div');
        overlay.id = 'game-scoreboard';
        overlay.className = 'scoreboard-overlay';

        const modal = document.createElement('div');
        modal.className = 'scoreboard-modal';

        // Title
        const title = document.createElement('h2');
        title.className = 'scoreboard-title';
        title.textContent = 'Game Over!';
        modal.appendChild(title);

        // Final score
        const scoreEl = document.createElement('div');
        scoreEl.className = 'scoreboard-winner';
        scoreEl.textContent = `Final Score: ${this.score}`;
        modal.appendChild(scoreEl);

        // Play again button
        const playAgainBtn = document.createElement('button');
        playAgainBtn.className = 'scoreboard-btn';
        playAgainBtn.textContent = 'Play Again';
        playAgainBtn.addEventListener('click', () => {
            overlay.remove();
            if (cleanupCelebration) cleanupCelebration();
            if (cleanupAmbient) cleanupAmbient();
            window.app.startSinglePlayer();
        });
        modal.appendChild(playAgainBtn);

        // Back to menu button
        const backBtn = document.createElement('button');
        backBtn.className = 'scoreboard-btn';
        backBtn.style.marginTop = '8px';
        backBtn.style.background = '#333';
        backBtn.textContent = 'Back to Menu';
        backBtn.addEventListener('click', () => {
            overlay.remove();
            if (cleanupCelebration) cleanupCelebration();
            if (cleanupAmbient) cleanupAmbient();
            window.app.showScreen('menu');
        });
        modal.appendChild(backBtn);

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Ambient fireworks with blimp
        const cleanupAmbient = startAmbientFireworks(overlay, [`Final Score: ${this.score}`]);
    }

    checkAutoEnd() {
        // Check if game should end automatically (empty rack + empty bag)
        if (this.rack.length === 0 && this.tileBag && this.tileBag.remaining() === 0) {
            this.showGameOver();
            return true;
        }
        return false;
    }

    bindEvents() {
        this.boundElements.rack = document.getElementById('rack');
        this.boundElements.board = document.getElementById('board');
        const rackElement = this.boundElements.rack;
        const boardElement = this.boundElements.board;
        const handlers = this.eventHandlers;

        // Rack tile selection (click to select, then click cell to place)
        handlers.rackClick = (e) => {
            const tileElement = e.target.closest('.tile');
            if (!tileElement) return;

            const slot = tileElement.closest('.rack-slot');
            const index = parseInt(slot.dataset.index);
            this.selectTile(index);
        };
        rackElement.addEventListener('click', handlers.rackClick);

        // Board cell clicks (tile picker, placement, pickup)
        handlers.boardClick = (e) => {
            const cell = e.target.closest('.cell');
            if (!cell) return;

            const row = parseInt(cell.dataset.row);
            const col = parseInt(cell.dataset.col);
            this.handleCellClick(row, col);
        };
        boardElement.addEventListener('click', handlers.boardClick);

        // === DRAG AND DROP EVENTS ===

        // Drag start from rack
        handlers.rackDragStart = (e) => {
            const tileElement = e.target.closest('.tile');
            if (!tileElement) return;

            const slot = tileElement.closest('.rack-slot');
            const index = parseInt(slot.dataset.index);

            this.draggedTile = this.rack[index];
            this.dragSource = 'rack';

            tileElement.classList.add('dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', this.draggedTile.id);
        };
        rackElement.addEventListener('dragstart', handlers.rackDragStart);

        // Drag start from board (pending tiles only)
        handlers.boardDragStart = (e) => {
            const tileElement = e.target.closest('.tile');
            if (!tileElement) return;

            const cell = e.target.closest('.cell');
            if (!cell || !cell.classList.contains('pending')) return;

            const row = parseInt(cell.dataset.row);
            const col = parseInt(cell.dataset.col);

            this.draggedTile = this.board.getTileAt(row, col);
            this.dragSource = { row, col };

            tileElement.classList.add('dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', this.draggedTile.id);
        };
        boardElement.addEventListener('dragstart', handlers.boardDragStart);

        // Drag end (cleanup)
        handlers.docDragEnd = (e) => {
            const tileElement = e.target.closest('.tile');
            if (tileElement) {
                tileElement.classList.remove('dragging');
            }
            this.draggedTile = null;
            this.dragSource = null;

            // Remove all drag-over classes
            document.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over', 'invalid');
            });
        };
        document.addEventListener('dragend', handlers.docDragEnd);

        // Drag over board cells
        handlers.boardDragOver = (e) => {
            e.preventDefault();
            const cell = e.target.closest('.cell');
            if (!cell || !this.draggedTile) return;

            const cellData = this.board.cells[cell.dataset.row][cell.dataset.col];

            // Can't drop on locked cells or cells with tiles (unless it's the source)
            if (cellData.locked || (cellData.tile && this.dragSource !== 'rack' &&
                !(this.dragSource.row == cell.dataset.row && this.dragSource.col == cell.dataset.col))) {
                cell.classList.add('drag-over', 'invalid');
                e.dataTransfer.dropEffect = 'none';
            } else if (!cellData.tile) {
                cell.classList.add('drag-over');
                e.dataTransfer.dropEffect = 'move';
            }
        };
        boardElement.addEventListener('dragover', handlers.boardDragOver);

        // Drag leave board cells
        handlers.boardDragLeave = (e) => {
            const cell = e.target.closest('.cell');
            if (cell) {
                cell.classList.remove('drag-over', 'invalid');
            }
        };
        boardElement.addEventListener('dragleave', handlers.boardDragLeave);

        // Drop on board cells
        handlers.boardDrop = (e) => {
            e.preventDefault();
            const cell = e.target.closest('.cell');
            if (!cell || !this.draggedTile) return;

            cell.classList.remove('drag-over', 'invalid');

            const row = parseInt(cell.dataset.row);
            const col = parseInt(cell.dataset.col);
            const cellData = this.board.cells[row][col];

            // Can't drop on locked cells or occupied cells
            if (cellData.locked || cellData.tile) return;

            // Remove tile from source
            if (this.dragSource === 'rack') {
                const index = this.rack.findIndex(t => t.id === this.draggedTile.id);
                if (index !== -1) {
                    this.rack.splice(index, 1);
                }
            } else {
                this.board.removeTile(this.dragSource.row, this.dragSource.col);
            }

            // Place tile on board
            this.board.placeTile(row, col, this.draggedTile, true);
            this.selectedTile = null;
            this.renderRack();

            // If it's a blank tile without an assigned letter, prompt for letter
            if (this.draggedTile.isBlank && !this.draggedTile.assignedLetter) {
                this.promptForBlankLetter(this.draggedTile, row, col);
            }
        };
        boardElement.addEventListener('drop', handlers.boardDrop);

        // Drag over rack (to return tiles or rearrange)
        handlers.rackDragOver = (e) => {
            e.preventDefault();
            if (!this.draggedTile) return;

            const slot = e.target.closest('.rack-slot');

            if (this.dragSource === 'rack') {
                // Rearranging within rack - highlight individual slot
                if (slot) {
                    // Clear other slot highlights
                    rackElement.querySelectorAll('.rack-slot.drag-over').forEach(s => {
                        if (s !== slot) s.classList.remove('drag-over');
                    });
                    slot.classList.add('drag-over');
                    e.dataTransfer.dropEffect = 'move';
                }
            } else {
                // Returning tile from board - highlight whole rack
                rackElement.classList.add('drag-over');
                e.dataTransfer.dropEffect = 'move';
            }
        };
        rackElement.addEventListener('dragover', handlers.rackDragOver);

        // Drag leave rack
        handlers.rackDragLeave = (e) => {
            const slot = e.target.closest('.rack-slot');
            if (slot && !slot.contains(e.relatedTarget)) {
                slot.classList.remove('drag-over');
            }
            // Only remove rack class if actually leaving the rack (not entering a child)
            if (!rackElement.contains(e.relatedTarget)) {
                rackElement.classList.remove('drag-over');
                rackElement.querySelectorAll('.rack-slot.drag-over').forEach(s => {
                    s.classList.remove('drag-over');
                });
            }
        };
        rackElement.addEventListener('dragleave', handlers.rackDragLeave);

        // Drop on rack (return tile from board or rearrange)
        handlers.rackDrop = (e) => {
            e.preventDefault();
            rackElement.classList.remove('drag-over');
            rackElement.querySelectorAll('.rack-slot.drag-over').forEach(s => {
                s.classList.remove('drag-over');
            });

            if (!this.draggedTile) return;

            if (this.dragSource === 'rack') {
                // Rearranging within rack
                const slot = e.target.closest('.rack-slot');
                if (!slot) return;

                const fromIndex = this.rack.findIndex(t => t.id === this.draggedTile.id);
                const toIndex = parseInt(slot.dataset.index);

                if (fromIndex !== -1 && fromIndex !== toIndex) {
                    // Remove tile from original position
                    const [tile] = this.rack.splice(fromIndex, 1);
                    // Insert at new position
                    this.rack.splice(toIndex, 0, tile);
                    this.renderRack();
                }
            } else {
                // Return tile from board to rack
                const tile = this.board.removeTile(this.dragSource.row, this.dragSource.col);
                if (tile) {
                    if (tile.isBlank) {
                        tile.assignedLetter = null;
                    }
                    this.rack.push(tile);
                    this.renderRack();
                }
            }
        };
        rackElement.addEventListener('drop', handlers.rackDrop);

        // Control buttons
        this.submitButton = document.getElementById('submit-word');
        handlers.submitClick = () => {
            this.submitWord();
        };
        this.submitButton.addEventListener('click', handlers.submitClick);

        this.swapButton = document.getElementById('swap-tiles');
        handlers.swapClick = () => {
            this.handleSwapButtonClick();
        };
        this.swapButton.addEventListener('click', handlers.swapClick);

        this.skipButton = document.getElementById('skip-turn');
        handlers.skipClick = () => {
            this.skipTurn();
        };
        this.skipButton.addEventListener('click', handlers.skipClick);

        this.finishButton = document.getElementById('finish-game');
        this.controlsStack = document.querySelector('.controls-stack');
        handlers.finishClick = () => {
            this.finishGame();
        };
        this.finishButton.addEventListener('click', handlers.finishClick);

        this.recallButton = document.getElementById('recall-tiles');
        handlers.recallClick = () => {
            this.recallTiles();
        };
        this.recallButton.addEventListener('click', handlers.recallClick);

        this.shuffleButton = document.getElementById('shuffle-rack');
        handlers.shuffleClick = () => {
            this.shuffleRack();
        };
        this.shuffleButton.addEventListener('click', handlers.shuffleClick);

        // Close tile picker when clicking outside
        handlers.docClick = (e) => {
            if (this.pickerTargetCell &&
                !this.pickerElement.contains(e.target) &&
                !e.target.closest('.cell')) {
                this.hideTilePicker();
            }
        };
        document.addEventListener('click', handlers.docClick);

        // Handle keyboard input
        handlers.docKeydown = (e) => {
            // Close tile picker or exit swap mode on escape
            if (e.key === 'Escape') {
                if (this.swapMode) {
                    this.exitSwapMode();
                }
                if (this.pickerTargetCell) {
                    this.hideTilePicker();
                }
                if (this.pendingBlankTile) {
                    this.cancelBlankAssignment();
                }
                return;
            }

            // Assign letter to blank tile (desktop keyboard)
            if (this.pendingBlankTile && /^[a-zA-Z]$/.test(e.key)) {
                this.assignLetterToBlank(e.key.toUpperCase());
                return;
            }

            // Type letter to select tile from picker
            if (this.pickerTargetCell && /^[a-zA-Z]$/.test(e.key)) {
                const letter = e.key.toUpperCase();
                // Find a tile in the rack with this letter (prefer exact match over blank)
                let tileIndex = this.rack.findIndex(t => t.letter === letter);
                if (tileIndex === -1) {
                    // Fall back to blank tile if available
                    tileIndex = this.rack.findIndex(t => t.isBlank);
                    if (tileIndex !== -1) {
                        // Pre-assign the typed letter to the blank
                        this.rack[tileIndex].assignedLetter = letter;
                    }
                }
                if (tileIndex !== -1) {
                    this.placeTileFromPicker(tileIndex);
                }
                return;
            }

            // Submit word on Enter
            if (e.key === 'Enter') {
                this.submitWord();
            }
        };
        document.addEventListener('keydown', handlers.docKeydown);

        // Hidden input for blank tile letter (triggers mobile keyboard)
        this.blankLetterInput = document.getElementById('blank-letter-input');
        if (this.blankLetterInput) {
            handlers.blankInput = (e) => {
                const value = e.target.value;
                if (value && /^[a-zA-Z]$/.test(value)) {
                    this.assignLetterToBlank(value.toUpperCase());
                }
                e.target.value = ''; // Clear for next use
            };
            this.blankLetterInput.addEventListener('input', handlers.blankInput);

            handlers.blankBlur = () => {
                // If user dismisses keyboard without typing, cancel the blank assignment
                if (this.pendingBlankTile) {
                    // Small delay to allow input event to fire first
                    setTimeout(() => {
                        if (this.pendingBlankTile) {
                            this.cancelBlankAssignment();
                        }
                    }, 100);
                }
            };
            this.blankLetterInput.addEventListener('blur', handlers.blankBlur);
        }
    }

    destroy() {
        const handlers = this.eventHandlers;
        const rackElement = this.boundElements.rack;
        const boardElement = this.boundElements.board;

        if (rackElement) {
            if (handlers.rackClick) rackElement.removeEventListener('click', handlers.rackClick);
            if (handlers.rackDragStart) rackElement.removeEventListener('dragstart', handlers.rackDragStart);
            if (handlers.rackDragOver) rackElement.removeEventListener('dragover', handlers.rackDragOver);
            if (handlers.rackDragLeave) rackElement.removeEventListener('dragleave', handlers.rackDragLeave);
            if (handlers.rackDrop) rackElement.removeEventListener('drop', handlers.rackDrop);
        }

        if (boardElement) {
            if (handlers.boardClick) boardElement.removeEventListener('click', handlers.boardClick);
            if (handlers.boardDragStart) boardElement.removeEventListener('dragstart', handlers.boardDragStart);
            if (handlers.boardDragOver) boardElement.removeEventListener('dragover', handlers.boardDragOver);
            if (handlers.boardDragLeave) boardElement.removeEventListener('dragleave', handlers.boardDragLeave);
            if (handlers.boardDrop) boardElement.removeEventListener('drop', handlers.boardDrop);
        }

        if (handlers.docDragEnd) document.removeEventListener('dragend', handlers.docDragEnd);
        if (handlers.docClick) document.removeEventListener('click', handlers.docClick);
        if (handlers.docKeydown) document.removeEventListener('keydown', handlers.docKeydown);

        if (this.submitButton && handlers.submitClick) {
            this.submitButton.removeEventListener('click', handlers.submitClick);
        }
        if (this.swapButton && handlers.swapClick) {
            this.swapButton.removeEventListener('click', handlers.swapClick);
        }
        if (this.skipButton && handlers.skipClick) {
            this.skipButton.removeEventListener('click', handlers.skipClick);
        }
        if (this.finishButton && handlers.finishClick) {
            this.finishButton.removeEventListener('click', handlers.finishClick);
        }
        if (this.recallButton && handlers.recallClick) {
            this.recallButton.removeEventListener('click', handlers.recallClick);
        }
        if (this.shuffleButton && handlers.shuffleClick) {
            this.shuffleButton.removeEventListener('click', handlers.shuffleClick);
        }
        if (this.blankLetterInput) {
            if (handlers.blankInput) this.blankLetterInput.removeEventListener('input', handlers.blankInput);
            if (handlers.blankBlur) this.blankLetterInput.removeEventListener('blur', handlers.blankBlur);
        }

        this.eventHandlers = {};
        this.boundElements = {};
        this.submitButton = null;
        this.swapButton = null;
        this.skipButton = null;
        this.finishButton = null;
        this.controlsStack = null;
        this.recallButton = null;
        this.shuffleButton = null;
        this.blankLetterInput = null;

        // Clear pending message timer
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }
    }

    selectTile(index) {
        // In swap mode, toggle tile for swapping instead of selecting
        if (this.swapMode) {
            const tile = this.rack[index];
            if (this.tilesMarkedForSwap.has(tile.id)) {
                this.tilesMarkedForSwap.delete(tile.id);
            } else {
                this.tilesMarkedForSwap.add(tile.id);
            }
            this.renderRack();
            return;
        }

        if (this.selectedTile && this.selectedTile.id === this.rack[index].id) {
            this.selectedTile = null;
        } else {
            this.selectedTile = this.rack[index];
        }
        this.renderRack();
    }

    handleCellClick(row, col) {
        const cellData = this.board.cells[row][col];

        // Hide picker if clicking elsewhere
        if (this.pickerTargetCell &&
            (this.pickerTargetCell.row !== row || this.pickerTargetCell.col !== col)) {
            this.hideTilePicker();
        }

        // If cell has a pending tile, pick it back up
        if (cellData.tile && cellData.element.classList.contains('pending')) {
            // If this is the blank tile awaiting letter assignment, clear that state
            if (this.pendingBlankTile && this.pendingBlankCell &&
                this.pendingBlankCell.row === row && this.pendingBlankCell.col === col) {
                this.pendingBlankTile = null;
                this.pendingBlankCell = null;
            }

            const tile = this.board.removeTile(row, col);
            if (tile.isBlank) {
                tile.assignedLetter = null; // Clear any assigned letter
            }
            this.rack.push(tile);
            this.renderRack();
            return;
        }

        // If cell is locked, ignore
        if (cellData.locked) return;

        // If we have a selected tile and cell is empty, place it
        if (this.selectedTile && !cellData.tile) {
            const index = this.rack.findIndex(t => t.id === this.selectedTile.id);
            if (index !== -1) {
                const tile = this.selectedTile;
                this.rack.splice(index, 1);
                this.board.placeTile(row, col, tile, true);
                this.selectedTile = null;
                this.renderRack();

                // If it's a blank tile, prompt for letter
                if (tile.isBlank && !tile.assignedLetter) {
                    this.promptForBlankLetter(tile, row, col);
                }
            }
            return;
        }

        // If no tile selected and cell is empty, toggle the tile picker
        if (!this.selectedTile && !cellData.tile && this.rack.length > 0) {
            // If picker is already showing for this cell, hide it
            if (this.pickerTargetCell &&
                this.pickerTargetCell.row === row && this.pickerTargetCell.col === col) {
                this.hideTilePicker();
            } else {
                this.showTilePicker(row, col);
            }
        }
    }

    showTilePicker(row, col) {
        const cellData = this.board.cells[row][col];
        const cellRect = cellData.element.getBoundingClientRect();

        this.pickerTargetCell = { row, col };

        // Build picker content
        this.pickerElement.innerHTML = '';
        this.rack.forEach((tile, index) => {
            const tileEl = createTileElement(tile, false);
            tileEl.dataset.rackIndex = index;
            tileEl.addEventListener('click', (e) => {
                e.stopPropagation();
                this.placeTileFromPicker(index);
            });
            this.pickerElement.appendChild(tileEl);
        });

        // Position the picker near the cell (single row layout matching rack style)
        const pickerWidth = this.rack.length * 48 + 24; // 40px tiles + 8px gaps + 32px padding
        const pickerHeight = 64; // 40px tiles + 24px padding

        // Determine if picker should appear above or below the cell
        const spaceBelow = window.innerHeight - cellRect.bottom;
        const spaceAbove = cellRect.top;
        const placeAbove = spaceBelow < pickerHeight + 20 && spaceAbove > spaceBelow;

        this.pickerElement.classList.remove('above', 'below');

        let top, left;
        if (placeAbove) {
            top = cellRect.top - pickerHeight - 10;
            this.pickerElement.classList.add('above');
        } else {
            top = cellRect.bottom + 10;
            this.pickerElement.classList.add('below');
        }

        // Center horizontally on the cell
        left = cellRect.left + (cellRect.width / 2) - (pickerWidth / 2);

        // Keep within viewport
        left = Math.max(10, Math.min(left, window.innerWidth - pickerWidth - 10));
        top = Math.max(10, Math.min(top, window.innerHeight - pickerHeight - 10));

        this.pickerElement.style.left = `${left}px`;
        this.pickerElement.style.top = `${top}px`;
        this.pickerElement.classList.add('visible');

        // Highlight the target cell
        cellData.element.classList.add('drag-over');
    }

    hideTilePicker() {
        this.pickerElement.classList.remove('visible');

        if (this.pickerTargetCell) {
            const cellData = this.board.cells[this.pickerTargetCell.row][this.pickerTargetCell.col];
            cellData.element.classList.remove('drag-over');
        }

        this.pickerTargetCell = null;
    }

    placeTileFromPicker(rackIndex) {
        if (!this.pickerTargetCell) return;

        const { row, col } = this.pickerTargetCell;
        const tile = this.rack[rackIndex];

        // Remove from rack and place on board
        this.rack.splice(rackIndex, 1);
        this.board.placeTile(row, col, tile, true);

        this.hideTilePicker();
        this.renderRack();

        // If it's a blank tile without an assigned letter, prompt for letter
        if (tile.isBlank && !tile.assignedLetter) {
            this.promptForBlankLetter(tile, row, col);
        }
    }

    promptForBlankLetter(tile, row, col) {
        this.pendingBlankTile = tile;
        this.pendingBlankCell = { row, col };

        // Add visual indicator
        const cellData = this.board.cells[row][col];
        cellData.element.classList.add('awaiting-letter');

        this.showMessage('Type a letter (A-Z) for the blank tile...', 'info');

        // Focus hidden input to trigger mobile keyboard
        if (this.blankLetterInput) {
            this.blankLetterInput.value = '';
            this.blankLetterInput.focus();

            // Scroll the cell into view after keyboard appears (flag-gated)
            if (flags.get('scrollBlankTileIntoView')) {
                setTimeout(() => {
                    cellData.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
        }
    }

    assignLetterToBlank(letter) {
        if (!this.pendingBlankTile) return;

        const { row, col } = this.pendingBlankCell;
        this.pendingBlankTile.assignedLetter = letter;

        // Remove visual indicator and re-render cell
        const cellData = this.board.cells[row][col];
        cellData.element.classList.remove('awaiting-letter');

        // Update the tile display
        const tileElement = cellData.element.querySelector('.tile');
        if (tileElement) {
            tileElement.textContent = letter;
        }

        this.showMessage(`Blank tile set to "${letter}"`, 'success');

        // Dismiss mobile keyboard
        if (this.blankLetterInput) {
            this.blankLetterInput.blur();
        }

        this.pendingBlankTile = null;
        this.pendingBlankCell = null;
    }

    cancelBlankAssignment() {
        if (!this.pendingBlankTile) return;

        const { row, col } = this.pendingBlankCell;

        // Remove visual indicator
        const cellData = this.board.cells[row][col];
        cellData.element.classList.remove('awaiting-letter');

        // Remove from board and return to rack
        const tile = this.board.removeTile(row, col);
        if (tile) {
            tile.assignedLetter = null;
            this.rack.push(tile);
            this.renderRack();
        }

        this.showMessage('Blank tile returned to rack', 'info');

        // Dismiss mobile keyboard
        if (this.blankLetterInput) {
            this.blankLetterInput.blur();
        }

        this.pendingBlankTile = null;
        this.pendingBlankCell = null;
    }

    handleSwapButtonClick() {
        if (this.swapMode) {
            // Already in swap mode - execute the swap if tiles are selected
            if (this.tilesMarkedForSwap.size > 0) {
                this.executeSwap();
            } else {
                this.exitSwapMode();
            }
        } else {
            this.enterSwapMode();
        }
    }

    skipTurn() {
        // Only available in multiplayer - overridden in MultiplayerGame
        this.showMessage('Skip is only available in multiplayer games.', 'info');
    }

    enterSwapMode() {
        // Check if there are tiles to swap with
        if (this.tileBag && this.tileBag.remaining() === 0) {
            this.showMessage('No tiles left in the bag to swap with!', 'error');
            return;
        }

        // Return any pending tiles to rack first
        this.recallTiles();
        this.selectedTile = null;
        this.hideTilePicker();

        this.swapMode = true;
        this.tilesMarkedForSwap.clear();
        this.swapButton.classList.add('swap-mode');
        this.showMessage('Click tiles to mark them for swapping, then click Swap again to confirm.', 'info');
        this.renderRack();
    }

    exitSwapMode() {
        this.swapMode = false;
        this.tilesMarkedForSwap.clear();
        this.swapButton.classList.remove('swap-mode');
        this.renderRack();
    }

    executeSwap() {
        if (!this.tileBag) {
            this.showMessage('Cannot swap tiles in this game mode.', 'error');
            this.exitSwapMode();
            return;
        }

        const tilesToSwap = this.rack.filter(t => this.tilesMarkedForSwap.has(t.id));
        const swapCount = tilesToSwap.length;

        if (swapCount === 0) {
            this.exitSwapMode();
            return;
        }

        // Check if there are enough tiles in the bag
        const availableTiles = this.tileBag.remaining();
        if (availableTiles < swapCount) {
            this.showMessage(`Only ${availableTiles} tiles left in the bag!`, 'error');
            return;
        }

        // Remove marked tiles from rack
        this.rack = this.rack.filter(t => !this.tilesMarkedForSwap.has(t.id));

        // Return them to the bag
        tilesToSwap.forEach(t => {
            if (t.isBlank) {
                t.assignedLetter = null; // Clear any assigned letter
            }
        });
        this.tileBag.returnTiles(tilesToSwap);

        // Draw new tiles
        const newTiles = this.tileBag.draw(swapCount);
        this.rack.push(...newTiles);

        this.exitSwapMode();
        this.showMessage(`Swapped ${swapCount} tile${swapCount > 1 ? 's' : ''}.`, 'success');
    }

    // Return all pending tiles to rack (available via window.game.recallTiles() for debugging)
    recallTiles() {
        // Clear any pending blank assignment
        this.pendingBlankTile = null;
        this.pendingBlankCell = null;

        const pending = this.board.getPendingTiles();
        pending.forEach(({ row, col }) => {
            const tile = this.board.removeTile(row, col);
            if (tile) {
                if (tile.isBlank) {
                    tile.assignedLetter = null; // Clear assigned letter
                }
                this.rack.push(tile);
            }
        });
        this.renderRack();
    }

    // Shuffle tiles in the rack using Fisher-Yates algorithm
    shuffleRack() {
        for (let i = this.rack.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.rack[i], this.rack[j]] = [this.rack[j], this.rack[i]];
        }
        this.renderRack();
    }

    submitWord() {
        const pending = this.board.getPendingTiles();

        if (pending.length === 0) {
            this.showMessage('Place some tiles on the board first!', 'error');
            return;
        }

        // Check for unassigned blank tiles
        for (const { row, col } of pending) {
            const tile = this.board.getTileAt(row, col);
            if (tile && tile.isBlank && !tile.assignedLetter) {
                this.showMessage('Assign a letter to the blank tile first!', 'error');
                return;
            }
        }

        // Check if tiles are in a line
        const validation = this.validatePlacement(pending);
        if (!validation.valid) {
            this.showMessage(validation.message, 'error');
            return;
        }

        // Check first move covers a center cell
        if (this.isFirstMove) {
            const coversCenter = pending.some(p => this.board.isCenterCell(p.row, p.col));
            if (!coversCenter) {
                this.showMessage('First word must cover the center star!', 'error');
                return;
            }
        }

        // Check connection to existing tiles (after first move)
        if (!this.isFirstMove) {
            const isConnected = this.checkConnection(pending);
            if (!isConnected) {
                this.showMessage('New tiles must connect to existing words!', 'error');
                return;
            }
        }

        // Get all words formed
        const words = this.findAllWords(pending, validation.direction);
        if (words.length === 0) {
            this.showMessage('Words must be at least 2 letters.', 'error');
            return;
        }

        // Validate all words against dictionary
        const invalidWords = words.filter(w => !isValidWord(w.word));
        if (invalidWords.length > 0) {
            const wordList = invalidWords.map(w => w.word).join(', ');
            this.showMessage(`Invalid word(s): ${wordList}`, 'error');
            return;
        }

        const totalScore = this.calculateScore(words);

        this.score += totalScore;
        document.getElementById('score').textContent = this.score;

        // Lock the tiles
        this.board.lockTiles();
        this.isFirstMove = false;

        // Draw new tiles
        const newTiles = this.tileBag.draw(pending.length);
        this.rack.push(...newTiles);
        this.renderRack();

        const wordList = words.map(w => w.word).join(', ');
        this.showMessage(`+${totalScore} points! Words: ${wordList}`, 'success');

        // Check for automatic game end (used all tiles with empty bag)
        if (!this.isMultiplayer) {
            this.checkAutoEnd();
        }
    }

    validatePlacement(pending) {
        if (pending.length === 1) {
            return { valid: true, direction: 'single' };
        }

        // Sort by position
        const sortedByRow = [...pending].sort((a, b) => a.row - b.row);
        const sortedByCol = [...pending].sort((a, b) => a.col - b.col);

        const sameRow = pending.every(p => p.row === pending[0].row);
        const sameCol = pending.every(p => p.col === pending[0].col);

        if (!sameRow && !sameCol) {
            return { valid: false, message: 'Tiles must be in a single row or column!' };
        }

        // Check for gaps (must be filled by existing tiles)
        if (sameRow) {
            const row = pending[0].row;
            const minCol = sortedByCol[0].col;
            const maxCol = sortedByCol[sortedByCol.length - 1].col;

            for (let col = minCol; col <= maxCol; col++) {
                if (!this.board.getTileAt(row, col)) {
                    return { valid: false, message: 'Tiles must be connected with no empty gaps!' };
                }
            }
            return { valid: true, direction: 'horizontal' };
        } else {
            const col = pending[0].col;
            const minRow = sortedByRow[0].row;
            const maxRow = sortedByRow[sortedByRow.length - 1].row;

            for (let row = minRow; row <= maxRow; row++) {
                if (!this.board.getTileAt(row, col)) {
                    return { valid: false, message: 'Tiles must be connected with no empty gaps!' };
                }
            }
            return { valid: true, direction: 'vertical' };
        }
    }

    checkConnection(pending) {
        for (const { row, col } of pending) {
            // Check adjacent cells for locked tiles
            const adjacent = [
                [row - 1, col],
                [row + 1, col],
                [row, col - 1],
                [row, col + 1]
            ];

            for (const [r, c] of adjacent) {
                if (r >= 0 && r < this.board.size && c >= 0 && c < this.board.size) {
                    const cellData = this.board.cells[r][c];
                    if (cellData.locked) return true;
                }
            }
        }
        return false;
    }

    findAllWords(pending, direction) {
        const words = [];
        // Find the main word
        if (direction === 'horizontal' || direction === 'single') {
            const mainWord = this.getWordAt(pending[0].row, pending[0].col, 'horizontal');
            if (mainWord.word.length > 1) {
                words.push(mainWord);
            }
        }
        if (direction === 'vertical' || direction === 'single') {
            const mainWord = this.getWordAt(pending[0].row, pending[0].col, 'vertical');
            if (mainWord.word.length > 1) {
                words.push(mainWord);
            }
        }

        // Find perpendicular words formed by each pending tile
        const perpDirection = direction === 'horizontal' ? 'vertical' : 'horizontal';
        for (const { row, col } of pending) {
            const perpWord = this.getWordAt(row, col, perpDirection);
            if (perpWord.word.length > 1 && !words.some(w =>
                w.word === perpWord.word && w.cells[0].row === perpWord.cells[0].row && w.cells[0].col === perpWord.cells[0].col
            )) {
                words.push(perpWord);
            }
        }

        return words;
    }

    getWordAt(row, col, direction) {
        const cells = [];
        let word = '';

        if (direction === 'horizontal') {
            // Find start of word
            let startCol = col;
            while (startCol > 0 && this.board.getTileAt(row, startCol - 1)) {
                startCol--;
            }

            // Collect word
            let c = startCol;
            while (c < this.board.size && this.board.getTileAt(row, c)) {
                const tile = this.board.getTileAt(row, c);
                word += tile.isBlank ? (tile.assignedLetter || '?') : tile.letter;
                cells.push({ row, col: c, tile });
                c++;
            }
        } else {
            // Find start of word
            let startRow = row;
            while (startRow > 0 && this.board.getTileAt(startRow - 1, col)) {
                startRow--;
            }

            // Collect word
            let r = startRow;
            while (r < this.board.size && this.board.getTileAt(r, col)) {
                const tile = this.board.getTileAt(r, col);
                word += tile.isBlank ? (tile.assignedLetter || '?') : tile.letter;
                cells.push({ row: r, col, tile });
                r++;
            }
        }

        return { word, cells };
    }

    calculateScore(words) {
        let totalScore = 0;

        for (const { cells } of words) {
            let wordScore = 0;
            let wordMultiplier = 1;

            for (const { row, col, tile } of cells) {
                const cellData = this.board.cells[row][col];
                let letterScore = tile.points;

                // Only apply bonuses if the tile was just placed (pending)
                if (cellData.element.classList.contains('pending')) {
                    const specialType = cellData.specialType;

                    if (specialType === 'double-letter') {
                        letterScore *= 2;
                    } else if (specialType === 'triple-letter') {
                        letterScore *= 3;
                    } else if (specialType === 'double-word') {
                        wordMultiplier *= 2;
                    } else if (specialType === 'triple-word') {
                        wordMultiplier *= 3;
                    }
                }

                wordScore += letterScore;
            }

            totalScore += wordScore * wordMultiplier;
        }

        // Bonus for using all 7 tiles
        const pending = this.board.getPendingTiles();
        if (pending.length === 7) {
            totalScore += 50;
        }

        return totalScore;
    }

    showMessage(text, type) {
        const MIN_DISPLAY_MS = 3000; // Minimum 3 seconds display time
        const now = Date.now();

        // Don't let info messages overwrite error messages too quickly
        // But success messages can always override (they indicate the error was resolved)
        if (type === 'info') {
            if (this.errorMessageUntil && now < this.errorMessageUntil) {
                return; // Don't overwrite recent error
            }
        }

        // Check if current message needs more display time
        if (type !== 'error' && this.messageShownAt && now < this.messageShownAt + MIN_DISPLAY_MS) {
            // Cancel any existing pending message
            if (this.pendingMessageTimer) {
                clearTimeout(this.pendingMessageTimer);
            }
            // Schedule this message for later
            const delay = this.messageShownAt + MIN_DISPLAY_MS - now;
            this.pendingMessageTimer = setTimeout(() => {
                this.pendingMessageTimer = null;
                this.showMessage(text, type);
            }, delay);
            return;
        }

        // Clear any pending message since we're showing a new one now
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }

        const messageElement = document.getElementById('message');
        messageElement.textContent = text;
        messageElement.className = 'message ' + type;
        this.messageShownAt = now;

        // Error messages get protection from being overwritten by info messages
        if (type === 'error') {
            this.errorMessageUntil = now + 5000; // 5 seconds
        } else if (type === 'success') {
            this.errorMessageUntil = null; // Clear error protection on success
        }
    }
}
